import React from "react";
import { Link, NavLink, Navigate } from "react-router-dom";
import {
  Shield, Building2, ListChecks, ClipboardCheck, FileBarChart, ServerCog, Plug, UserPlus, Gauge, Info, Trash2, RotateCw,
} from "lucide-react";
import Header from "@/components/Header";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import { useAuth } from "@/contexts/AuthContext";

/**
 * v1.2 — Super Admin workspace chrome.
 * Replaces AppShell entirely for `/super-admin/*` routes. Distinct visual
 * treatment per the IA: near-black sidebar `#0F1729`, accent `#FF8A4C`,
 * "SUPER ADMIN" pill in the header (handled by Header itself).
 *
 * Tab labels are the user-visible source of truth. NO per-tab "pr-X" stage
 * pills — these were a temporary aid during PR-A/B development and were
 * confusing once the underlying pages landed. The
 * `frontend/scripts/check-no-mock-leak.js` lint guard rejects any
 * reintroduction of `pr-[a-z]` literal pill labels in this file.
 */

const TABS = [
  { to: "/super-admin",                       end: true,  label: "Organisations",       icon: Building2,    testid: "satab-orgs" },
  { to: "/super-admin/overview",                          label: "Overview",            icon: Gauge,        testid: "satab-overview" },
  { to: "/super-admin/integrations",                      label: "Integration vault",   icon: Plug,         testid: "satab-integrations" },
  { to: "/super-admin/provision",                         label: "Provision admin",     icon: UserPlus,     testid: "satab-provision" },
  { to: "/super-admin/audit-log",                         label: "Audit log",           icon: FileBarChart, testid: "satab-audit" },
  { to: "/super-admin/privacy-acceptances",               label: "Privacy acceptances", icon: ClipboardCheck,testid: "satab-privacy" },
  { to: "/super-admin/notifications-log",                 label: "Notifications log",   icon: ListChecks,   testid: "satab-notifications" },
  { to: "/super-admin/notifications-replay",              label: "Notifications replay",icon: RotateCw,     testid: "satab-notifications-replay" },
  { to: "/super-admin/data-cleanup",                      label: "Data cleanup",        icon: Trash2,       testid: "satab-data-cleanup" },
  { to: "/super-admin/maintenance",                       label: "Maintenance",         icon: ServerCog,    testid: "satab-maintenance" },
  { to: "/super-admin/build",                             label: "Build & version",     icon: Info,         testid: "satab-build" },
];

export default function SuperAdminLayout({ children }) {
  const { user } = useAuth();

  // Hard-block at the chrome level — defence-in-depth on top of any in-page checks.
  if (user && user.role !== "super_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F8FA] dark:bg-navy-900" data-testid="super-admin-shell">
      <ImpersonationBanner />
      <div className="flex flex-1">
        <aside
          className="hidden md:flex flex-col w-60 flex-shrink-0 text-white"
          style={{ backgroundColor: "#0F1729" }}
          data-testid="super-admin-sidebar"
        >
          <Link to="/super-admin" className="px-5 py-4 border-b border-white/10 flex items-center gap-2">
            <span
              className="w-7 h-7 rounded-md flex items-center justify-center text-xs font-bold"
              style={{ backgroundColor: "#FF8A4C", color: "#0F1729" }}
            >
              <Shield className="w-4 h-4" />
            </span>
            <span className="font-semibold tracking-wide">Super admin</span>
          </Link>
          <nav className="flex-1 overflow-y-auto py-3 px-2">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <NavLink
                  key={t.to}
                  to={t.to}
                  end={t.end}
                  data-testid={t.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors mb-0.5 ${
                      isActive
                        ? "text-white"
                        : "text-white/70 hover:text-white"
                    }`
                  }
                  style={({ isActive }) => isActive ? { backgroundColor: "rgba(255,138,76,0.18)" } : undefined}
                >
                  <Icon className="w-4 h-4" /> {t.label}
                </NavLink>
              );
            })}
          </nav>
          <div className="border-t border-white/10 p-3">
            <Link
              to="/dashboard"
              className="flex items-center gap-2 text-xs text-white/60 hover:text-white"
              data-testid="super-admin-exit"
            >
              ← Exit super admin
            </Link>
          </div>
        </aside>
        <div className="flex-1 min-w-0 flex flex-col">
          <Header context="super_admin" />
          <main className="flex-1 px-4 md:px-6 lg:px-8 py-6 md:py-8 pb-24 md:pb-8 max-w-[1600px] w-full">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
